// Sales Activities rail — FE-SCRM-024 (+ FE-SCRM-053 contact_id mode).
//
// Bound-anchor rail for Prospect / Opportunity / Contact detail pages.
// Shows the recent activities for the bound entity with the same
// quick-add affordance as the Activities tab, but pre-anchored to
// the host entity. The ``contact_id`` variant powers FE-SCRM-053's
// Contact detail page and uses BE-SCRM-023's existing
// ``?contact_id=`` filter on the activities endpoint.

import { useState } from "react";

import {
  DashboardErrorState, DashboardLoadingState,
} from "@/features/dashboard";

import { ActivityComposerModal } from "./ActivityComposerModal";
import { ActivityListTable } from "./ActivityListTable";
import { QuickAddActivityMenu } from "./QuickAddActivityMenu";
import {
  useActivityListQuery,
  useCancelActivityMutation,
  useCompleteActivityMutation,
  useCreateActivityMutation,
  useDeleteActivityMutation,
  useRevertActivityMutation,
} from "./hooks";
import type { ActivityCreateInput, ActivityType } from "./types";


export interface ActivityRailProps {
  prospect_id?: string;
  opportunity_id?: string;
  contact_id?: string;
}


export function ActivityRail({
  prospect_id, opportunity_id, contact_id,
}: ActivityRailProps) {
  const [composer_open, setComposerOpen] = useState<ActivityType | null>(
    null,
  );

  if (!prospect_id && !opportunity_id && !contact_id) {
    return (
      <div
        data-testid="activity-rail-missing-anchor"
        className="text-xs text-text-tertiary"
      >
        Sales Activities require a Prospect, Opportunity, or Contact anchor.
      </div>
    );
  }

  // Anchor precedence matches the activities filter contract: a host
  // page passing more than one anchor narrows by the most specific
  // first. Contact is the narrowest, then opportunity, then prospect.
  const list_query = useActivityListQuery(
    contact_id
      ? { contact_id }
      : prospect_id
        ? { prospect_id }
        : { opportunity_id },
  );
  const create_mutation = useCreateActivityMutation();
  const complete_mutation = useCompleteActivityMutation();
  const revert_mutation = useRevertActivityMutation();
  const cancel_mutation = useCancelActivityMutation();
  const delete_mutation = useDeleteActivityMutation();

  const rows = list_query.data?.rows ?? [];
  const pending_id = (
    complete_mutation.variables?.activity_id
    ?? (typeof revert_mutation.variables === "string"
      ? revert_mutation.variables : null)
    ?? (typeof cancel_mutation.variables === "string"
      ? cancel_mutation.variables : null)
    ?? (typeof delete_mutation.variables === "string"
      ? delete_mutation.variables : null)
    ?? null
  );

  function handleComposerSubmit(input: ActivityCreateInput) {
    create_mutation.mutate(input, {
      onSuccess: () => setComposerOpen(null),
    });
  }

  return (
    <div
      data-testid="activity-rail"
      className="flex flex-col gap-3"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Sales Activities
        </h3>
        <QuickAddActivityMenu
          on_pick={(t) => setComposerOpen(t ?? "follow_up")}
        />
      </div>

      {list_query.isLoading ? (
        <DashboardLoadingState variant="card" />
      ) : list_query.isError ? (
        <DashboardErrorState
          title="Couldn't load Sales Activities"
          description="Refresh to retry."
          retry={() => list_query.refetch()}
        />
      ) : (
        <ActivityListTable
          rows={rows}
          is_pending_id={pending_id}
          on_complete={(a) =>
            complete_mutation.mutate({ activity_id: a.activity_id })
          }
          on_revert={(a) => revert_mutation.mutate(a.activity_id)}
          on_cancel={(a) => cancel_mutation.mutate(a.activity_id)}
          on_delete={(a) => delete_mutation.mutate(a.activity_id)}
        />
      )}

      {composer_open !== null && (
        <ActivityComposerModal
          initial_type={composer_open}
          bound_prospect_id={prospect_id}
          bound_opportunity_id={opportunity_id}
          bound_contact_id={contact_id}
          is_submitting={create_mutation.isPending}
          on_submit={handleComposerSubmit}
          on_cancel={() => setComposerOpen(null)}
        />
      )}
    </div>
  );
}
